/**
 * Customer Quotation — prototype `vQuote()`, contract §9, infra C.6.
 *
 * The combined review: contract-priced provision lines and RFQ-sourced lines together, each
 * with an editable quantity and selling price, defaulted from the item's category margin.
 *
 * FE/BE split, which is sharper here than on any other screen:
 *   FE — live pricing-rule flags while typing, the category-margin default, and display
 *        conversion into the customer's currency (all from lib/pricing.ts + lib/fx.ts).
 *   BE — the SAVED total and the enforcement on send. The client's arithmetic is never
 *        written back; every edit round-trips and the server returns the authoritative
 *        totals, and `POST /send` answers 409 if a rule is broken regardless of what the
 *        button looked like.
 */

import { useNavigate, useParams } from 'react-router-dom'
import { ReadOnlyGate } from '../../components/ReadOnlyGate'
import { lockReason } from '../../lib/readOnly'
import { ContextBar } from '../../components/ContextBar'
import { PageHead, Placeholder } from '../../components/ui'
import { Badge } from '../../components/Badge'
import { useEnquiry } from '../../lib/api/useEnquiries'
import { useQuotation, useQuotedItems } from '../../lib/api/useQuotation'
import { isLiveFeature } from '../../lib/api/liveAvailability'
import { QuotationEditor } from './QuotationEditor'
import { CustomerHistoryCard } from './CustomerHistoryCard'

export function QuotePage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()

  const enquiryQuery = useEnquiry(id)
  const quotationQuery = useQuotation(id)

  const quotation = quotationQuery.data
  // IDENTITY for the lookup — never `customer_name` (fix round, task-11-report.md: I4).
  // `enquiryQuery.data.header.customer` is identity too (`useEnquiries.ts`'s `toEnquiryHeader`
  // never falls back to a display label either) — it only matters before `quotation` has loaded,
  // when there is nothing to query yet anyway.
  const customerId = quotation?.customer ?? enquiryQuery.data?.header.customer ?? ''
  const customerLabel = quotation?.customer_name ?? enquiryQuery.data?.header.customer ?? ''
  // A8 (docs/bosun-required-api-round-2.md) — no live endpoint yet; passing `null` while
  // unavailable reuses the hook's own `enabled: !!customer` gate rather than firing a request
  // that would 404 against live. See `lib/api/liveAvailability.ts`.
  const historyAvailable = isLiveFeature('quotedItems')
  const historyQuery = useQuotedItems(historyAvailable ? customerId || null : null)

  /* ---------- states ---------- */

  if (enquiryQuery.isPending || quotationQuery.isPending) return <Placeholder>Loading quotation…</Placeholder>

  if (enquiryQuery.error || !enquiryQuery.data) {
    const error = enquiryQuery.error as Error | null
    return (
      <Placeholder>
        <b>Could not load {id}.</b> Check the mock backend is running (<b>npm run dev:all</b>).
        {error ? <div style={{ marginTop: 6 }}>{error.message}</div> : null}
      </Placeholder>
    )
  }

  const enquiry = enquiryQuery.data

  // Nothing to quote yet — the allocation hasn't been chosen.
  if (quotationQuery.error || !quotation) {
    const error = quotationQuery.error as (Error & { code?: string }) | null
    const nothingToQuote = error && 'code' in error && error.code === 'NOTHING_TO_QUOTE'
    return (
      <>
        <ContextBar enquiry={enquiry} active="quote" />
        {/* The ⓘ matters most here — "why is there nothing to quote?" is exactly what it answers. */}
        <PageHead help="quote" title="Customer Quotation" />
        <Placeholder>
          {nothingToQuote ? (
            <>
              <b>Nothing to quote yet.</b> This requirement has no contract-priced lines and no chosen allocation —
              pick suppliers on the{' '}
              <span className="linkish" onClick={() => navigate(`/compare/${id}`)}>
                comparison workbench
              </span>{' '}
              first.
            </>
          ) : (
            <>
              <b>Could not build the quotation.</b>
              {error ? <div style={{ marginTop: 6 }}>{error.message}</div> : null}
            </>
          )}
        </Placeholder>
      </>
    )
  }

  /**
   * RFQ lines the quotation left out — a line with no saved allocation is skipped when the
   * quotation is built, and silently losing a customer's `cust_sno` is worse than any
   * pricing error, so it has to be visible.
   */
  const quotedCodes = new Set(quotation.lines.map((l) => l.item_code))
  const omitted = enquiry.items.filter(
    (l) => l.route === 'rfq' && l.item_code && !quotedCodes.has(l.item_code),
  )
  /*
    Lines that ARE on the quotation but carry no price — the new state, since every enquiry line
    now travels and an unallocated one arrives at zero (`quotationFromAllocation.ts`).

    This is the actionable one: those rows are on the customer's copy and would go out at nothing.
    The `omitted` check above is kept for quotations built BEFORE that change, where lines really
    were dropped — a different fact, needing different advice.
  */
  const unpriced = quotation.lines.filter((l) => l.sell_unit.amount <= 0)

  return (
    <>
      <ContextBar enquiry={enquiry} active="quote" />
      {/* Every control below is disabled once the enquiry is decided — `lib/readOnly.ts`. */}
      <ReadOnlyGate reason={lockReason(enquiry.status, 'quote')}>

      <PageHead help="quote"
        title="Customer Quotation"
        subtitle={
          <>
            {/* Rewritten 2026-09-04: the markup/commission/convenience controls moved into the
                Quotation commission panel, and the Settings markup it pointed at is now read-only
                (`D23`) — the sentence described a screen that no longer exists. */}
            Combined review — <b>edit the selling price</b> per line (quantity is read-only on
            this API). Commission, markup, taxes and terms are set on the panels above and apply to{' '}
            <b>this quotation only</b>. Pricing rules from{' '}
            <span className="linkish" onClick={() => navigate('/settings')}>
              Settings
            </span>{' '}
            are enforced live.
          </>
        }
      />

      <div style={{ marginTop: -8, marginBottom: 12 }}>
        <Badge tone={quotation.status === 'draft' ? 'warn' : 'ok'}>
          {quotation.status === 'draft' ? 'Draft' : quotation.status}
        </Badge>{' '}
        <span style={{ color: 'var(--muted)', fontSize: 12 }}>{quotation.id}</span>
      </div>

      {/* Only reachable on a quotation built before every line started travelling. Kept as the
          safety net it always was, but no longer telling anyone to allocate: rebuilding is what
          brings a missing line back now. */}
      {omitted.length > 0 ? (
        <div className="banner" style={{ background: 'var(--warnbg)', borderColor: 'var(--warn)' }} role="alert">
          ⚠ <b>{omitted.length} RFQ line(s) are missing from this quotation</b>:{' '}
          {omitted.map((l) => `#${l.cust_sno} ${l.item_code}`).join(', ')}. This quotation predates
          the change that carries every line through.{' '}
          <span className="linkish" onClick={() => navigate(`/compare/${id}`)}>
            Rebuild it from the comparison
          </span>{' '}
          to include them.
        </div>
      ) : null}

      {/* On the quotation, at zero. They will go to the customer priced at nothing unless someone
          types a price — so this names them rather than leaving them to be spotted in the table. */}
      {unpriced.length > 0 ? (
        <div className="banner" style={{ background: 'var(--warnbg)', borderColor: 'var(--warn)' }} role="alert">
          ⚠ <b>{unpriced.length} line(s) have no price yet</b> — nobody quoted them, so they are
          here at zero: {unpriced.map((l) => `#${l.cust_sno} ${l.item_code}`).join(', ')}. Set a
          selling price on each, or{' '}
          <span className="linkish" onClick={() => navigate(`/compare/${id}`)}>
            allocate a supplier
          </span>{' '}
          first.
        </div>
      ) : null}

      <QuotationEditor enquiryId={id} quotation={quotation} enquiry={enquiry} locked={lockReason(enquiry.status, 'quote') !== null} />

      <CustomerHistoryCard
        customer={customerLabel}
        items={historyQuery.data}
        isPending={historyQuery.isPending}
        unavailable={!historyAvailable}
      />
      </ReadOnlyGate>
    </>
  )
}
